import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import AdvertBanner from "@/components/AdvertBanner";
import Footer from "@/components/Footer";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle, MessageSquare, Headphones, KeyRound } from "lucide-react";

const SupportPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("new"); // new | existing
  const [submitted, setSubmitted] = useState(false);
  const [ticketId, setTicketId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    category: "general"
  });

  // Existing ticket viewer state
  const [lookup, setLookup] = useState({ ticket_id: "", email: "" });
  const [ticket, setTicket] = useState(null);
  const [replyMsg, setReplyMsg] = useState("");
  const [lookupErr, setLookupErr] = useState("");
  const [posting, setPosting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in Name, Subject, and Message");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/support/tickets`, form);
      setTicketId(res.data.ticket_id);
      setSubmitted(true);
      toast.success("Ticket submitted!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLookup = async () => {
    setLookupErr("");
    setTicket(null);
    if (!lookup.ticket_id.trim() || !lookup.email.trim()) {
      setLookupErr("Enter both your ticket code and the email you used.");
      return;
    }
    try {
      const r = await axios.get(`${API}/support/tickets/${lookup.ticket_id.trim()}?email=${encodeURIComponent(lookup.email.trim())}`);
      setTicket(r.data);
    } catch (e) {
      setLookupErr(e.response?.data?.detail || "Could not load ticket");
    }
  };

  const handleUserReply = async () => {
    if (!replyMsg.trim()) { toast.error("Type a reply first"); return; }
    setPosting(true);
    try {
      const r = await axios.post(`${API}/support/tickets/${ticket.ticket_id}/user-reply`, {
        email: lookup.email.trim(),
        message: replyMsg.trim(),
      });
      toast.success(r.data.message);
      setReplyMsg("");
      // Refresh thread
      const rr = await axios.get(`${API}/support/tickets/${ticket.ticket_id}?email=${encodeURIComponent(lookup.email.trim())}`);
      setTicket(rr.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post reply");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC]" data-testid="support-page">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <AdvertBanner />
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Headphones className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">Support Center</h1>
            <p className="text-gray-500 max-w-md mx-auto">Open a new ticket or read & reply to one you've already submitted.</p>
          </div>

          {/* Mode toggle */}
          <div className="flex justify-center gap-2 mb-8">
            <Button variant={mode === "new" ? "default" : "outline"} className={mode === "new" ? "bg-green-600 text-white" : ""} onClick={() => { setMode("new"); setSubmitted(false); }} data-testid="mode-new">
              <MessageSquare className="h-4 w-4 mr-2" /> New Ticket
            </Button>
            <Button variant={mode === "existing" ? "default" : "outline"} className={mode === "existing" ? "bg-green-600 text-white" : ""} onClick={() => setMode("existing")} data-testid="mode-existing">
              <KeyRound className="h-4 w-4 mr-2" /> View / Reply to Existing Ticket
            </Button>
          </div>

          {/* Contact cards */}
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <a href="mailto:support@fixedraws.com" className="block" data-testid="support-email-link">
              <Card className="bg-emerald-50/60 border-emerald-200/60 hover:border-emerald-400 transition-colors cursor-pointer h-full">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
                    <Send className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Email Support</p>
                    <p className="text-sm text-gray-500">support@fixedraws.com</p>
                    <p className="text-xs text-emerald-600 font-medium mt-1">We reply within 24 hours</p>
                  </div>
                </CardContent>
              </Card>
            </a>
            <Card className="bg-blue-50/50 border-blue-200/50 h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                  <MessageSquare className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Ticket System</p>
                  <p className="text-sm text-gray-500">Submit, read & reply right here</p>
                  <p className="text-xs text-blue-600 font-medium mt-1">Fastest way to reach us</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* NEW TICKET MODE */}
          {mode === "new" && !submitted && (
            <Card className="bg-white border-gray-200" data-testid="ticket-form-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MessageSquare className="h-5 w-5 text-green-600" /> Open a Support Ticket
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your name" data-testid="ticket-name" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@example.com" data-testid="ticket-email" />
                      <p className="text-[11px] text-gray-500">You'll need this email to read replies on this page.</p>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+234..." data-testid="ticket-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                        <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Inquiry</SelectItem>
                          <SelectItem value="payment">Payment Issue</SelectItem>
                          <SelectItem value="code">Code / Access Issue</SelectItem>
                          <SelectItem value="technical">Technical Problem</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Subject *</Label>
                    <Input value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} placeholder="Brief summary of your issue" data-testid="ticket-subject" />
                  </div>
                  <div className="space-y-2">
                    <Label>Message *</Label>
                    <Textarea value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Describe your issue in detail..." rows={5} data-testid="ticket-message" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full h-12 text-base font-semibold" data-testid="submit-ticket-btn">
                    {submitting ? "Submitting..." : <><Send className="h-4 w-4 mr-2" /> Submit Ticket</>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {mode === "new" && submitted && (
            <Card className="bg-white border-gray-200 text-center" data-testid="ticket-success">
              <CardContent className="py-12">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Ticket Submitted!</h2>
                <p className="text-gray-500 mb-2">Copy and save this ticket code — you'll need it to read replies on this page:</p>
                <div className="bg-gray-100 rounded-lg inline-block px-6 py-3 font-mono text-lg font-bold text-gray-900 mb-6" data-testid="ticket-id-display">
                  {ticketId}
                </div>
                <p className="text-sm text-gray-500 mb-8">A copy of this code has also been emailed to you.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => { setMode("existing"); setLookup({ ticket_id: ticketId, email: form.email }); }} className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6" data-testid="goto-thread-btn">
                    View My Ticket
                  </Button>
                  <Button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", subject: "", message: "", category: "general" }); }} variant="outline" className="rounded-full px-6" data-testid="new-ticket-btn">
                    Submit Another Ticket
                  </Button>
                  <Button onClick={() => navigate("/")} variant="ghost" className="rounded-full px-6" data-testid="back-home-btn">
                    Back to Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* EXISTING TICKET MODE */}
          {mode === "existing" && (
            <div className="space-y-6">
              <Card className="bg-white border-gray-200" data-testid="ticket-lookup-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <KeyRound className="h-5 w-5 text-amber-600" /> Find Your Ticket
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ticket Code *</Label>
                      <Input value={lookup.ticket_id} onChange={e => setLookup({...lookup, ticket_id: e.target.value})} placeholder="tkt_xxxxxxxxxx" className="font-mono" data-testid="lookup-ticket-id" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email used when you opened the ticket *</Label>
                      <Input type="email" value={lookup.email} onChange={e => setLookup({...lookup, email: e.target.value})} placeholder="you@example.com" data-testid="lookup-email" />
                    </div>
                  </div>
                  <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={handleLookup} data-testid="lookup-btn">Load Ticket Thread</Button>
                  {lookupErr && <p className="text-sm text-red-600" data-testid="lookup-error">{lookupErr}</p>}
                </CardContent>
              </Card>

              {ticket && ticket.ticket_id && !ticket.requires_email && (
                <Card className="bg-white border-gray-200" data-testid="ticket-thread-card">
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <CardTitle className="text-lg" data-testid="thread-subject">{ticket.subject}</CardTitle>
                        <p className="text-xs text-gray-500 mt-1 font-mono">{ticket.ticket_id}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${ticket.status === "open" ? "bg-amber-100 text-amber-700" : ticket.status === "resolved" ? "bg-green-100 text-green-700" : ticket.status === "in_progress" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}`} data-testid="thread-status">{(ticket.status || "open").replace("_", " ").toUpperCase()}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Original message */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
                        <span className="font-bold text-gray-700">{ticket.name} (you, original)</span>
                        <span>{new Date(ticket.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap select-text" data-testid="thread-original">{ticket.message}</p>
                    </div>
                    {/* Reply list */}
                    {(ticket.replies || []).map((r, idx) => (
                      <div key={idx} className={`rounded-lg p-3 border ${r.from === "admin" ? "bg-indigo-50 border-indigo-200" : r.from === "system" ? "bg-amber-50 border-amber-200" : "bg-white border-gray-200"}`}>
                        <div className="flex items-center justify-between text-[11px] text-gray-500 mb-1">
                          <span className="font-bold">{r.from === "admin" ? "Fixedraws Support" : r.from === "system" ? "Auto-Reply" : "You"}</span>
                          <span>{new Date(r.sent_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-gray-800 whitespace-pre-wrap select-text" data-testid={`thread-reply-${idx}`}>{r.message}</p>
                      </div>
                    ))}
                    {(ticket.replies || []).length === 0 && (
                      <p className="text-xs text-gray-500 text-center py-3">No replies yet — our team will respond shortly.</p>
                    )}
                    {/* User reply box */}
                    <div className="border-t border-gray-100 pt-4 space-y-2">
                      <Label className="text-sm font-bold text-gray-700">Post a reply</Label>
                      <Textarea rows={4} value={replyMsg} onChange={e => setReplyMsg(e.target.value)} placeholder="Type your reply..." data-testid="user-reply-input" />
                      <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleUserReply} disabled={posting} data-testid="user-reply-btn">
                        {posting ? "Sending..." : <><Send className="h-4 w-4 mr-2" /> Send Reply</>}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {ticket && ticket.requires_email && (
                <Card className="bg-amber-50 border-amber-200" data-testid="lookup-need-email">
                  <CardContent className="py-6 text-center text-sm text-amber-800">
                    This ticket is protected. Please enter the email used when you opened it.
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SupportPage;
